<template>
  <div>
    <v-card flat>
      <v-data-table :headers="headers" :items="auditLogsList" :items-per-page="10" v-model:page="page"
        @update:options="onTableOptionsChange" class="elevation-0 custom-data-table" :no-data-text="noDataText"
        :height="tableHeight" fixed-header :loading="loading" density="compact">
        <!-- 🔥 TOP SLOT -->
        <template v-slot:top>
          <div class="d-flex justify-space-between align-center">
            <!-- TITLE -->
            <span class="text-h5">Audit Logs</span>

            <!-- 🔥 FILTERS (NO WRAP FIX) -->
            <div class="d-flex flex-column">

              <!-- First Row -->
              <div class="d-flex align-center flex-nowrap">

                <!-- FROM -->
                <v-text-field v-model="fromDateDisplay" label="From" variant="outlined" density="compact"
                  class="mr-2 mt-5" style="width: 180px" maxlength="10"
                  @input="(e) => formatDateInput(e, 'fromDateDisplay')"
                  @blur="validateAndFormatDate('fromDateDisplay', 'fromDate')">
                  <template #append-inner>
                    <v-menu v-model="showFromPicker" :close-on-content-click="false">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" @mousedown.prevent>
                          mdi-calendar
                        </v-icon>
                      </template>

                      <v-date-picker v-model="tempFromDate" hide-header @update:model-value="
                        (val) =>
                          setDateFromPicker(
                            val,
                            'fromDateDisplay',
                            'fromDate',
                            'showFromPicker'
                          )
                      " />
                    </v-menu>
                  </template>
                </v-text-field>

                <!-- TO -->
                <v-text-field v-model="toDateDisplay" label="To" variant="outlined" density="compact" class="mr-2 mt-5"
                  style="width: 180px" maxlength="10" @input="(e) => formatDateInput(e, 'toDateDisplay')"
                  @blur="validateAndFormatDate('toDateDisplay', 'toDate')">
                  <template #append-inner>
                    <v-menu v-model="showToPicker" :close-on-content-click="false">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" @mousedown.prevent>
                          mdi-calendar
                        </v-icon>
                      </template>

                      <v-date-picker v-model="tempToDate" hide-header @update:model-value="
                        (val) =>
                          setDateFromPicker(
                            val,
                            'toDateDisplay',
                            'toDate',
                            'showToPicker'
                          )
                      " />
                    </v-menu>
                  </template>
                </v-text-field>

                <v-autocomplete v-model="selectedOrganizationId" :items="organizationOptions" label="Organization"
                  variant="outlined" density="compact" class="mr-2" :style="{
                    width: selectedAdvanceFilter ? '420px' : '220px',
                    transition: 'width .25s ease'
                  }" item-title="organization_name" item-value="organization_id" clearable hide-no-data hide-details />

                <v-autocomplete v-if="!selectedAdvanceFilter" v-model="selectedAction" :items="actionOptions"
                  label="Action" variant="outlined" density="compact" class="mr-2" style="width:180px"
                  item-title="label" item-value="value" clearable hide-details />

                <v-btn color="primary" class="ml-1" style="height:40px" :disabled="(selectedAdvanceFilter && selectedSubAdvanceFilter.length === 0) ||
                  (!selectedAdvanceFilter && selectedSubAdvanceFilter.length > 0)
                  " @click="loadAuditLogs">
                  Apply
                </v-btn>

              </div>

              <!-- Second Row -->
              <div class="d-flex align-center flex-wrap mt-3 ml-0">

                <v-autocomplete v-model="selectedAdvanceFilter" :items="advanceFilterOptions" label="Advance Filter"
                  variant="outlined" density="compact" class="mr-3" style="width: 260px" item-title="label"
                  item-value="value" clearable hide-details hide-no-data />

                <v-autocomplete v-model="selectedSubAdvanceFilter" :items="subAdvanceFilterOptions"
                  label="Filter Action" multiple chips closable-chips variant="outlined" density="compact" class="mr-3"
                  style="width:350px" item-title="label" item-value="value" clearable hide-details />
              </div>

            </div>
          </div>
        </template>

        <!-- ACTION CHIP -->
        <template v-slot:[`item.log_action`]="{ item }">
          <v-chip size="small" color="primary" variant="tonal">
            {{ item.log_action }}
          </v-chip>
        </template>
        <template v-slot:[`item.user_email_id`]="{ item }">
          <div>
            <div class="font-weight-medium">
              {{ item.user_name || "-" }}
            </div>
            <div class="text-caption text-grey">
              {{ item.user_email_id || "-" }}
            </div>
          </div>
        </template>

        <!-- DATE -->
        <template v-slot:[`item.created_on`]="{ item }">
          <div>
            <div class="font-weight-medium">
              {{ formatDate(item.created_on) }}
            </div>
            <div class="text-caption text-grey">
              {{ formatTime(item.created_on) }}
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import GetAuditLogs from "@/mixins/Organization/GetAuditLogs";
import GetOrganizations from "@/mixins/Organization/GetOrganizations";

export default {
  name: "AuditLog",
  mixins: [GetAuditLogs, GetOrganizations],

  data() {
    return {
      loading: false,
      page: 1,
      fromDate: "",
      toDate: "",
      fromDateDisplay: "",
      toDateDisplay: "",
      tempFromDate: null,
      selectedAction: null,
      selectedAdvanceFilter: null,
      selectedSubAdvanceFilter: [],
      tempToDate: null,
      showFromPicker: false,
      showToPicker: false,
      selectedOrganizationId: null,
      organizations: [],
      windowHeight: window.innerHeight,

      headers: [
        { title: "User", key: "user_email_id" },
        { title: "Organization", key: "organization_name" },
        { title: "Action", key: "log_action" },
        { title: "Description", key: "log_description" },
        { title: "Date", key: "created_on" },
      ],
    };
  },
  watch: {
    selectedAdvanceFilter(newValue) {
      this.selectedSubAdvanceFilter = [];

      if (newValue) {
        this.selectedAction = null;
      }
    }
  },
  computed: {
    // 🔥 SORTED DROPDOWN
    actionOptions() {
      return [
        { label: "All", value: null },
        { label: "CREATE", value: "CREATE" },
        { label: "UPDATE", value: "UPDATE" },
        { label: "DELETE", value: "DELETE" },
        { label: "DEACTIVATE", value: "DEACTIVATE" },
      ];
    },
    advanceFilterOptions() {
      return [
        { label: "Resident", value: "RESIDENT" },
        { label: "Medication", value: "MEDICATION" },
        { label: "Medication tool", value: "MEDICATION_TOOL" },
        { label: "Medication chatbot", value: "MEDICATION_CHATBOT" },
      ]
    },
    subAdvanceFilterOptions() {

      switch (this.selectedAdvanceFilter) {

        case "RESIDENT":
          return [
            { label: "Resident Create", value: "RESIDENT_CREATE" },
            { label: "Resident Update", value: "RESIDENT_UPDATE" },
            { label: "Resident Deactivate", value: "RESIDENT_DEACTIVATE" },
            { label: "Resident Bulk Update", value: "RESIDENT_BULK_UPDATE" },
            { label: "Resident Bulk Deactivate", value: "RESIDENT_BULK_DEACTIVATE" },
          ];

        case "MEDICATION":
          return [
            { label: "Medication Create", value: "MEDICATION_CREATE" },
            { label: "Medication Update", value: "MEDICATION_UPDATE" },
            { label: "Medication Deactivate", value: "MEDICATION_DEACTIVATE" },
          ];

        case "MEDICATION_TOOL":
          return [
            { label: "Medication Import", value: "PDF_MEDICATION_IMPORT" },
            { label: "Medication Unimport", value: "PDF_MEDICATION_UNDO_IMPORT" }
          ];

        case "MEDICATION_CHATBOT":
          return [
            { label: "Query", value: "AI_CHATBOT_QUERY" },
          ];

        default:
          return [];
      }

    },
    organizationOptions() {
      const sorted = [...(this.organizations || [])].sort((a, b) =>
        a.organization_name.localeCompare(b.organization_name)
      );

      return [{ organization_name: "All", organization_id: null }, ...sorted];
    },

    noDataText() {
      return "No audit logs found.";
    },

    tableHeight() {
      return this.windowHeight - 220;
    },
  },

  async mounted() {
    this.setDefaultDates();
    await this.fetchOrganizations();
    this.organizations = this.organizationsList;
    this.loadAuditLogs();
  },

  methods: {
    async onTableOptionsChange(options) {
      const fromTimestamp = this.fromDate
        ? new Date(this.fromDate + "T00:00:00").getTime()
        : undefined;

      const toTimestamp = this.toDate
        ? new Date(this.toDate + "T23:59:59").getTime()
        : undefined;

      await this.fetchNextAuditLogs({
        organization_id: this.selectedOrganizationId || undefined,
        from_date: fromTimestamp,
        to_date: toTimestamp,
        keywords:
          this.selectedSubAdvanceFilter.length > 0
            ? this.selectedSubAdvanceFilter
            : undefined,
        log_action: this.selectedAction || undefined,
      });

      this.page = options.page;
    },
    setDefaultDates() {
      const now = new Date();

      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(now.getDate()).padStart(2, "0")}`;

      const firstDay = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-01`;

      this.fromDate = firstDay;
      this.toDate = today;

      const [fy, fm, fd] = firstDay.split("-");
      this.fromDateDisplay = `${fm}/${fd}/${fy}`;

      const [ty, tm, td] = today.split("-");
      this.toDateDisplay = `${tm}/${td}/${ty}`;

      this.tempFromDate = new Date(now.getFullYear(), now.getMonth(), 1);
      this.tempToDate = now;
    },

    async loadAuditLogs() {

      // Validation
      if (this.selectedAdvanceFilter && this.selectedSubAdvanceFilter.length === 0) {
        this.showSnackbar("Please select Filter Action.", "error");
        return;
      }

      if (!this.selectedAdvanceFilter && this.selectedSubAdvanceFilter.length > 0) {
        this.showSnackbar("Please select Advance Filter.", "error");
        return;
      }

      this.loading = true;

      try {
        const fromTimestamp = this.fromDate
          ? new Date(this.fromDate + "T00:00:00").getTime()
          : undefined;

        const toTimestamp = this.toDate
          ? new Date(this.toDate + "T23:59:59").getTime()
          : undefined;

        await this.fetchAuditLogs({
          organization_id: this.selectedOrganizationId || undefined,
          from_date: fromTimestamp,
          to_date: toTimestamp,
          keywords:
            this.selectedSubAdvanceFilter.length > 0
              ? this.selectedSubAdvanceFilter
              : undefined,
          log_action: this.selectedAction || undefined,
        });
      } finally {
        this.loading = false;
      }
    },

    formatDate(ts) {
      if (!ts) return "-";
      const d = new Date(Number(ts));

      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();

      return `${mm}/${dd}/${yyyy}`;
    },

    formatTime(ts) {
      if (!ts) return "-";
      const d = new Date(Number(ts));

      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");

      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;

      return `${hours}:${minutes} ${ampm}`;
    },

    formatDateInput(e, key) {
      let v = e.target.value.replace(/\D/g, "").slice(0, 8);
      if (v.length > 2) v = v.slice(0, 2) + "/" + v.slice(2);
      if (v.length > 4) v = v.slice(0, 5) + "/" + v.slice(5);
      this[key] = v;
    },

    validateAndFormatDate(displayKey, actualKey) {
      const value = this[displayKey];
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return;

      const [m, d, y] = value.split("/").map(Number);
      this[actualKey] = `${y}-${String(m).padStart(2, "0")}-${String(
        d
      ).padStart(2, "0")}`;
    },

    setDateFromPicker(val, displayKey, actualKey, pickerKey) {
      const d = new Date(val);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();

      this[displayKey] = `${String(m).padStart(2, "0")}/${String(day).padStart(
        2,
        "0"
      )}/${y}`;
      this[actualKey] = `${y}-${String(m).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`;
      this[pickerKey] = false;
    },
  },
};
</script>

<style scoped>
.custom-data-table td {
  white-space: normal !important;
  word-break: break-word;
}
</style>